import React, { useLayoutEffect, useSyncExternalStore } from "react";
import { t } from "./i18n";
import { APPEARANCE_KEY, APPEARANCE_MODES, parseAppearance, readStoredAppearance, resolveAppearance, toggledAppearance, type AppearanceMode } from "./appearance-mode";
import "./appearance.css";

// One store for every switch (sidebar, phone top row, quiet challenge), other tabs and the system setting.
const DARK_QUERY = "(prefers-color-scheme: dark)";
const safeStorage: Pick<Storage, "getItem" | "setItem" | "removeItem"> = {
  getItem: (key) => { try { return localStorage.getItem(key); } catch { return null; } },
  setItem: (key, value) => { try { localStorage.setItem(key, value); } catch { /* private mode: still works for this visit */ } },
  removeItem: (key) => { try { localStorage.removeItem(key); } catch { /* ignore */ } },
};
const systemDark = () => typeof matchMedia === "function" && matchMedia(DARK_QUERY).matches;
let mode: AppearanceMode | null = null;
const listeners = new Set<() => void>();
const currentMode = () => (mode ??= readStoredAppearance(safeStorage));
const notify = () => { for (const listener of listeners) listener(); };

export function setAppearance(next: AppearanceMode) {
  mode = next;
  safeStorage.setItem(APPEARANCE_KEY, next);
  notify();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  const media = typeof matchMedia === "function" ? matchMedia(DARK_QUERY) : null;
  const storage = (event: StorageEvent) => { if (event.key === APPEARANCE_KEY) { mode = parseAppearance(event.newValue); listener(); } };
  media?.addEventListener("change", listener);
  addEventListener("storage", storage);
  return () => { listeners.delete(listener); media?.removeEventListener("change", listener); removeEventListener("storage", storage); };
}
// Snapshot as one string so useSyncExternalStore compares by value.
const snapshot = () => `${currentMode()}:${systemDark() ? "dark" : "light"}`;

const noSubscribe = () => () => {};
const lightSnapshot = () => "light:light";
/** `enabled: false` (the public tour) never reads, migrates or writes the stored choice and stays light. */
export function useAppearance(enabled = true) {
  const [stored, system] = useSyncExternalStore(enabled ? subscribe : noSubscribe, enabled ? snapshot : lightSnapshot, () => "system:light").split(":") as [AppearanceMode, "dark" | "light"];
  const effective = resolveAppearance(stored, system === "dark");
  const set = (next: AppearanceMode) => { if (enabled) setAppearance(next); };
  return { mode: stored, effective, set, toggle: () => set(toggledAppearance(effective)) };
}

/** Paint the teacher workspace in the chosen appearance while it is mounted (public pages and /tour stay light). */
export function useApplyAppearance(active: boolean) {
  const { effective } = useAppearance(active);
  useLayoutEffect(() => {
    const root = document.documentElement;
    delete root.dataset.appearanceBoot;
    if (!active) return;
    root.dataset.appearance = effective;
    root.style.colorScheme = effective;
    return () => { delete root.dataset.appearance; root.style.colorScheme = ""; };
  }, [active, effective]);
}

/** Before the workspace chunk loads: only the loading card follows the choice, so a dark screen does not flash white. */
export function bootAppearance() {
  if (resolveAppearance(currentMode(), systemDark()) === "dark") document.documentElement.dataset.appearanceBoot = "dark";
}

// Round-cap strokes like the hand-drawn icon set: half-filled circle, moon, sun.
const ICONS: Record<AppearanceMode, React.ReactNode> = {
  system: <><circle cx="12" cy="12" r="8.5" /><path className="appearance-icon-fill" d="M12 3.5a8.5 8.5 0 0 0 0 17Z" /></>,
  dark: <path className="appearance-icon-fill" d="M19.5 14.6A8 8 0 0 1 9.4 4.5a8 8 0 1 0 10.1 10.1Z" />,
  light: <><circle cx="12" cy="12" r="4.2" /><path d="M12 2.8v2.4M12 18.8v2.4M2.8 12h2.4M18.8 12h2.4M5.5 5.5l1.7 1.7M16.8 16.8l1.7 1.7M5.5 18.5l1.7-1.7M16.8 7.2l1.7-1.7" /></>,
};
export function AppearanceIcon({ mode: icon }: { mode: AppearanceMode }) {
  return <svg className="appearance-icon" viewBox="0 0 24 24" aria-hidden="true">{ICONS[icon]}</svg>;
}

/** 隨系統｜黑夜｜白天: three small segments next to the account entry. */
export function AppearanceSwitch({ className = "" }: { className?: string }) {
  const { mode: current, effective, set } = useAppearance();
  const labels: Record<AppearanceMode, string> = { system: t("隨系統"), dark: t("黑夜"), light: t("白天") };
  return <div className={`appearance-switch ${className}`} role="group" aria-label={t("外觀：{mode}", { mode: current === "system" ? t("隨系統（目前{effective}）", { effective: effective === "dark" ? labels.dark : labels.light }) : labels[current] })}>
    {APPEARANCE_MODES.map((value) => <button key={value} type="button" aria-pressed={current === value} title={labels[value]} aria-label={labels[value]} onClick={() => set(value)}>
      <AppearanceIcon mode={value} /><span>{labels[value]}</span>
    </button>)}
  </div>;
}
